import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { SWRConfig } from 'swr';
import { Provider } from 'react-redux';
import TagManager from 'react-gtm-module';
// root app page
import App from 'app';
// providers
import { ProjectStepperContextProvider } from './hooks/useProjectStepperState';
import { ToastContextProvider } from 'hooks/useToast';
import { ProjectProvider } from 'hooks/useProjectDetails';
// store
import { store } from 'redux-store/store';
// styles
import 'swiper/css';
import './index.css';
import { datadogRum } from '@datadog/browser-rum';
import { datadogLogs } from '@datadog/browser-logs';
import { reactPlugin } from '@datadog/browser-rum-react';

declare global {
  interface Window {
    jQuery?: any;
  }
}

// Load jQuery before GTM if not already loaded (required by GTM tags)
const loadJQueryIfNeeded = (): Promise<void> => {
  return new Promise((resolve) => {
    if (window.jQuery) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://code.jquery.com/jquery-3.7.1.min.js';
    script.integrity = 'sha256-/JqT3SQfawRcv/BIHPThkBvs0OEvtFFmqPF/lYI/Cxo=';
    script.crossOrigin = 'anonymous';
    script.onload = () => resolve();
    script.onerror = () => {
      console.warn('Failed to load jQuery, GTM may not work correctly');
      resolve(); // Continue anyway
    };
    document.head.appendChild(script);
  });
};

const tagManagerArgs = {
  gtmId: 'GTM-T8FPR29',
};
console.log('App Env: ', process.env.REACT_APP_ENV);
if (process.env.REACT_APP_ENV === 'Production') {
  loadJQueryIfNeeded().then(() => {
    TagManager.initialize(tagManagerArgs);
  });
}

const domNode = document.getElementById('root')!;
const root = createRoot(domNode);

// Datadog configuration
const datadogApplicationId = process.env.REACT_APP_DATADOG_APPLICATION_ID;
const datadogClientToken = process.env.REACT_APP_DATADOG_CLIENT_TOKEN;
const datadogEnv = process.env.REACT_APP_DATADOG_ENV;

// Normalize environment name to lowercase (Datadog tags must be lowercase)
const normalizedEnv = datadogEnv ? datadogEnv.toLowerCase() : 'dev';

// Validate Datadog environment variables
const isDatadogConfigured = datadogApplicationId && datadogClientToken;

if (!isDatadogConfigured) {
  console.warn(
    '⚠️ Datadog is not configured. Missing required environment variables:',
    {
      REACT_APP_DATADOG_APPLICATION_ID: !datadogApplicationId,
      REACT_APP_DATADOG_CLIENT_TOKEN: !datadogClientToken,
    }
  );
} else {
  try {
    // Initialize Datadog Browser Logs first (must be initialized before RUM)
    datadogLogs.init({
      clientToken: datadogClientToken,
      site: 'datadoghq.com',
      service: 'frontend',
      env: normalizedEnv,
      forwardErrorsToLogs: true,
    });

    // Initialize Datadog RUM
    datadogRum.init({
      applicationId: datadogApplicationId,
      clientToken: datadogClientToken,
      site: 'datadoghq.com',
      service: 'frontend',
      env: normalizedEnv,
      sessionSampleRate: 100,
      sessionReplaySampleRate: 100,
      defaultPrivacyLevel: 'allow',
      plugins: [reactPlugin({ router: true })],
    });

    // Set up global error handler to send errors to Datadog
    window.addEventListener('error', (event) => {
      datadogLogs.logger.error('Unhandled error', {
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        error: event.error,
      });
    });

    // Set up unhandled promise rejection handler
    window.addEventListener('unhandledrejection', (event) => {
      datadogLogs.logger.error('Unhandled promise rejection', {
        reason: event.reason,
        promise: event.promise,
      });
    });

    console.log('✅ Datadog initialized successfully');
  } catch (error) {
    console.error('❌ Failed to initialize Datadog:', error);
  }
}

root.render(
  <BrowserRouter>
    <Provider store={store}>
      <ProjectProvider>
        <ProjectStepperContextProvider>
          <ToastContextProvider>
            <SWRConfig value={{ provider: () => new Map() }}>
              <App />
            </SWRConfig>
          </ToastContextProvider>
        </ProjectStepperContextProvider>
      </ProjectProvider>
    </Provider>
  </BrowserRouter>
);
